// ─── Écran racine My Finance : les POSTES budgétaires ─────────────
// Orchestre les données (écoutes bornées à l'espace) et rend toutes les
// modales en SŒURS — jamais imbriquées, y compris le sélecteur de date.
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Alert,
} from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, SelecteurDate, CalqueChoix, useUI, useT, useAuth, useEspace, confirmerSuppression, confirmerChoix,
  usePileRetour,
} from '@mop/core';
import {
  ecouterBudgets, ecouterOperations, creerBudget, modifierBudget, supprimerBudget,
  ajouterOperation, modifierOperation, supprimerOperation, creerRemboursement,
} from './services/budget';
import { formatEuro } from './services/calculs';
import { cleErreur } from './services/erreurs';
import {
  moisCourant, prevuDuMoisParBudget, estDue, montantPrevu, moisEntre, decalerMois,
  moisVoisinAvecPrevu, lignesDeBudget,
  occurrencesDuMois,
} from './services/occurrences';
import {
  ecouterLignes, ecouterExceptions, creerLigne, modifierLigne, supprimerLigne,
  definirException, retirerException,
} from './services/lignes';
import {
  ecouterRattachements, rattacher as creerRattachement, modifierRattachement, detacher, appliquerDiffRattachements,
} from './services/rattachements';
import { proposerOperations } from './services/propositions';
import { rattachementsAvecOperation, estSoldee } from './services/repartition';
import { prefSession, definirPrefSession } from './services/prefsSession';
import { ecouterComptes } from './services/comptes';
import { ecouterConfigFinance } from './services/configFinance';
import FeuilleRattachement from './FeuilleRattachement';
import ArbreBudgetsMois from './ArbreBudgetsMois';
import BarreRecherche from './BarreRecherche';
import FeuilleRattacherLignes from './FeuilleRattacherLignes';
import FeuilleRattacherOperations from './FeuilleRattacherOperations';
import { emojiBudget , definirCategoriesPerso } from './data/categories';
import FeuilleBudget from './FeuilleBudget';
import FeuilleOperation from './FeuilleOperation';
import FeuilleLigne from './FeuilleLigne';
// ⚠️ DÉBRANCHÉ le 11/08 : le 🧾 mène désormais à la CONFIGURATION des
// prévisions (demande de Cédric). Cet écran — opérations réelles de
// l'enveloppe + « qui doit à qui » — n'a plus de porte d'entrée, mais il reste
// COMPLET et FONCTIONNEL. Cédric a décidé de geler ce sujet tant que Budget,
// Comptes et Suivi ne sont pas finis ; il se rebranchera à ce moment-là.
import EcranBudget from './EcranBudget';
import EcranConfigBudget from './EcranConfigBudget';
import EcranEnveloppe from './EcranEnveloppe';
import FicheOccurrence from './FicheOccurrence';
import FicheOperation from './FicheOperation';
import FicheLignePrevue from './FicheLignePrevue';
import FicheBudget from './FicheBudget';

// Ouvrir les prévisions ORPHELINES. Ce n'est PAS une enveloppe : rien n'est
// créé dans Firestore. Une vraie enveloppe « Sans budget » serait une donnée
// fictive — partageable, renommable, à supprimer un jour — pour représenter
// une ABSENCE. Le moteur sait déjà grouper sous la clé `null`
// (`prevuDuMoisParBudget`) et `lignesDeBudget(lignes, null)` les retrouve :
// il ne manquait que la porte d'entrée.
const SANS_BUDGET = '__sans__';

const HIT_MOIS = { top: 8, bottom: 8, left: 8, right: 8 };

// « Août 2026 » se relit ; « 2026-08 » se déchiffre.
const libelleMois = (mk, t) => {
  const [y, m] = String(mk || '').split('-').map(Number);
  const noms = t('commun.moisLongs');
  return `${(Array.isArray(noms) ? noms[m - 1] : '') || mk || ''} ${y || ''}`.trim();
};

export default function EcranFinances() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { espaceActif, tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [budgets, setBudgets] = useState([]);
  const [operations, setOperations] = useState([]);
  const [ouvert, setOuvert] = useState(null);      // poste ouvert (fiche)
  const [feuilleB, setFeuilleB] = useState(null);  // { initial } édition d'un poste
  const [feuilleO, setFeuilleO] = useState(null);  // { initial, budgetId } opération
  const [dateReq, setDateReq] = useState(null);    // sélecteur de date (modale sœur)
  const [lignes, setLignes] = useState([]);        // la PRÉVISION
  const [exceptions, setExceptions] = useState([]);
  const [feuilleL, setFeuilleL] = useState(null);  // ligne prévue en édition
  const [operationsDe, setOperationsDe] = useState(null); // écran « réel » — gelé, plus atteignable
  // Le MODE de lecture, partagé par les deux niveaux : la liste des budgets
  // et l'intérieur d'une enveloppe. C'est une préférence de lecture, pas un
  // écran — elle survit donc à la navigation, dans les deux sens.
  const [mode, setMode] = useState('mois');   // 'mois' | 'liste'
  const [ficheLigne, setFicheLigne] = useState(null);     // ligne prévue en LECTURE
  const [ficheOcc, setFicheOcc] = useState(null);  // occurrence en LECTURE
  const [occARattacher, setOccARattacher] = useState(null);
  const [lotPour, setLotPour] = useState(null);   // rattachement en LOT sur une prévision
  const [ficheOp, setFicheOp] = useState(null);    // operation reelle en LECTURE
  const [ficheBudget, setFicheBudget] = useState(null); // budget en LECTURE (15/08)
  const [opARattacher, setOpARattacher] = useState(null); // opération → lignes (16/08)
  const [rattachements, setRattachements] = useState([]);
  // Les comptes ne servent qu'à la modale de rattachement : filtrer par compte
  // (§6.5) et afficher son emoji devant chaque opération. L'onglet Budget ne
  // les montre nulle part ailleurs — mais il lui FAUT la vraie liste, pas un
  // tableau vide : le filtre serait alors muet sans le dire.
  const [comptes, setComptes] = useState([]);
  // Config finance de l'espace : categories perso, masquages, sous-cats.
  const [configCategories, setConfigCategories] = useState(null);
  // Le mois AFFICHÉ. Les totaux le suivent : ils étaient figés sur le mois
  // courant, ce que rien à l'écran ne disait.
  const [moisVu, setMoisVu] = useState(moisCourant);
  const sautAvant = useMemo(() => moisVoisinAvecPrevu(lignes, moisVu, -1), [lignes, moisVu]);
  const sautApres = useMemo(() => moisVoisinAvecPrevu(lignes, moisVu, 1), [lignes, moisVu]);
  const [recherche, setRecherche] = useState('');
  // Filtre et tri de la liste des budgets (Cédric, 16/08) : la barre commune
  // porte ses deux boutons ici aussi.
  const [filtreBudgets, setFiltreBudgets] = useState('tous');  // tous | echeances
  const [triBudgets, setTriBudgets] = useState('defaut');      // defaut | nom | prevu
  const [calque, setCalque] = useState(null);
  // La SYNTHÈSE se plie depuis l'en-tête (Cédric, 16/08) — choix mémorisé le
  // temps de la session, écrans épurés oblige.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseBudget', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseBudget', !v);
    return !v;
  });
  // D'où l'on vient, pour y revenir à la fermeture (Cédric, 13/08) :
  // ouvrir un écran depuis un autre oblige à FERMER le premier (deux
  // <Modal> imbriquées figent l'écran), sinon on retombe sur la liste.
  const retour = usePileRetour();

  useEffect(() => ecouterBudgets(tenantId, setBudgets), [tenantId]);
  useEffect(() => ecouterOperations(tenantId, setOperations), [tenantId]);
  useEffect(() => ecouterLignes(tenantId, setLignes), [tenantId]);
  useEffect(() => ecouterExceptions(tenantId, setExceptions), [tenantId]);
  useEffect(() => ecouterRattachements(tenantId, setRattachements), [tenantId]);
  useEffect(() => ecouterComptes(tenantId, setComptes), [tenantId]);
  useEffect(() => ecouterConfigFinance(tenantId, (c) => {
    setConfigCategories(c);
    // Le registre d'affichage : les emojis des categories perso, partout.
    definirCategoriesPerso(c.categoriesPerso);
  }), [tenantId]);

  // La liste des enveloppes montre le PRÉVU du mois courant, pas le cumul des
  // dépenses passées : cet onglet estime, il ne constate pas. C'est ce que le
  // total précédent laissait croire — il additionnait les débits d'un relevé.
  const prevuParBudget = useMemo(
    () => prevuDuMoisParBudget({ lignes, exceptions, mk: moisVu }),
    [lignes, exceptions, moisVu],
  );

  // Membres de l'espace = payeurs et destinataires d'accès possibles.
  const membres = useMemo(() => {
    const e = espaceActif || {};
    return (e.membreUids || []).map(uid => ({
      uid,
      nom: (e.noms || {})[uid] || (uid === user?.uid ? (user?.prenom || user?.email || '') : uid.slice(0, 5)),
    }));
  }, [espaceActif, user]);

  // La recherche porte sur le NOM du budget : la liste est courte, chercher
  // dedans par montant n'aurait pas de sens. Puis le filtre (« avec échéance
  // ce mois-ci » — la carte à 0,00 € d'un budget entièrement hors mois
  // n'apprend rien) et le tri, sans toucher à l'ordre d'origine par défaut.
  const budgetsVus = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    let liste = q ? budgets.filter(b => String(b.nom || '').toLowerCase().includes(q)) : budgets;
    if (filtreBudgets === 'echeances') liste = liste.filter(b => prevuParBudget.has(b.id));
    if (triBudgets === 'nom') {
      liste = [...liste].sort((a, b) => String(a.nom || '').localeCompare(String(b.nom || '')));
    } else if (triBudgets === 'prevu') {
      liste = [...liste].sort((a, b) => (prevuParBudget.get(b.id) || 0) - (prevuParBudget.get(a.id) || 0));
    }
    return liste;
  }, [budgets, recherche, filtreBudgets, triBudgets, prevuParBudget]);

  // Le total du mois, toutes enveloppes VUES : il suit donc la recherche.
  // Les prevus du mois par SENS (Cedric, 15/08) : le net seul (credit −
  // debit) cache l'ampleur des deux flux — +4 000 et −2 500 font le meme net
  // que +2 000 et −500. Ignorees et absentes exclues, comme dans le net.
  const prevusParSens = useMemo(() => {
    // Le meme perimetre que le total de gauche (Cedric, 15/08) : les flux
    // suivent la RECHERCHE — budgets visibles, orphelines seulement quand on
    // ne filtre pas, comme `totalMois`.
    const visibles = new Set(budgetsVus.map(b => b.id));
    const filtrees = lignes.filter(l => (l?.budgetId
      ? visibles.has(l.budgetId)
      : !recherche.trim()));
    const occ = occurrencesDuMois({ lignes: filtrees, actuals: [], exceptions, mk: moisVu });
    let debit = 0, credit = 0;
    for (const o of occ) {
      if (o.statut === 'ignoree' || o.statut === 'absente') continue;
      if (o.ligne?.sens === 'revenu') credit += Math.abs(o.prevu || 0);
      else debit += Math.abs(o.prevu || 0);
    }
    return { debit, credit };
  }, [lignes, exceptions, moisVu, budgetsVus, recherche]);

  const totalMois = useMemo(
    () => budgetsVus.reduce((n, b) => n + (prevuParBudget.get(b.id) || 0), 0)
      + (recherche.trim() ? 0 : (prevuParBudget.get(null) || 0)),
    [budgetsVus, prevuParBudget, recherche],
  );

  const opsDe = (budgetId) => operations.filter(o => o.budgetId === budgetId);
  // Les motifs de rattachement deviennent des phrases : « rattachement.
  // montantNul » affiche tel quel est un code interne presente comme un
  // message (Cedric, 15/08). Un motif inconnu retombe sur le brut.
  const echec = (e) => {
    const cle = cleErreur(e);
    Alert.alert(t('commun.erreur'), cle ? t(cle) : (e?.message || ''));
  };

  const enregistrerBudget = async (b) => {
    try {
      if (feuilleB?.initial?.id) await modifierBudget(feuilleB.initial.id, b);
      else {
        const espaceId = tenantId || await assurerEspace();
        await creerBudget({ ...b, espaceId, creePar: user?.uid });
      }
      setFeuilleB(null);
      // Revenir d'où l'on vient — la fiche du budget si l'édition en sort.
      retour.revenir();
    } catch (e) { echec(e); }
  };

  const enregistrerOperation = async (o) => {
    try {
      const budgetId = feuilleO?.budgetId ?? feuilleO?.initial?.budgetId ?? null;
      if (feuilleO?.initial?.id) await modifierOperation(feuilleO.initial.id, o, feuilleO.initial);
      else await ajouterOperation({
        ...o, budgetId, espaceId: tenantId,
        payeurNom: (membres.find(m => m.uid === o.payeurUid) || {}).nom || '',
        creePar: user?.uid,
      });
      setFeuilleO(null);
      retour.revenir();
    } catch (e) { echec(e); }
  };

  const enregistrerLigne = async (l) => {
    try {
      if (feuilleL?.initial?.id) await modifierLigne(feuilleL.initial.id, l);
      else {
        const espaceId = tenantId || await assurerEspace();
        await creerLigne({ ...l, espaceId, creePar: user?.uid });
      }
      setFeuilleL(null);
      retour.revenir();
    } catch (e) { echec(e); }
  };

  // Supprimer un budget : VERROUILLÉ tant qu'il porte des lignes prévues
  // (Cédric, 15/08). `supprimerBudget` n'efface pas les lignes : elles
  // deviendraient orphelines dans « Sans budget » sans que rien ne le dise.
  // Le refus s'explique — c'est un garde-fou, pas une panne.
  const demanderSuppressionBudget = (b) => {
    const n = lignesDeBudget(lignes, b.id).length;
    if (n > 0) {
      Alert.alert(t('commun.supprimer'), t('finances.supprBudgetLignes', { nom: b.nom, n }));
      return;
    }
    confirmerSuppression(t('commun.supprimer'), b.nom,
      () => supprimerBudget(b.id, tenantId).catch(echec));
  };

  const rembourser = (budget) => async (d) => {
    try {
      await creerRemboursement({
        espaceId: tenantId, budgetId: budget.id,
        deUid: d.deUid, deNom: d.de, aUid: d.aUid, aNom: d.a,
        montant: d.montant, creePar: user?.uid,
      });
    } catch (e) { echec(e); }
  };

  // Les mois où une prévision est réellement due, bornés à ±24 mois : une
  // ligne sans fin produirait sinon une liste sans fin. C'est cette liste
  // qui définit « hors plage » (§9.1) et alimente le choix du mois.
  const moisDusDe = (ligne) => {
    if (!ligne) return [];
    const debut = decalerMois(moisCourant(), -24);
    return moisEntre(debut, decalerMois(moisCourant(), 24)).filter(mk => estDue(ligne, mk));
  };

  // ⚠️ Une fiche garde en état l'objet qu'on lui a donné à l'OUVERTURE. Après
  // une édition, Firestore met la liste à jour mais pas ce cliché : l'écran
  // d'où l'on vient montrait donc encore les anciennes valeurs (signalé par
  // Cédric le 13/08). On re-résout à chaque rendu depuis la liste vivante.
  // Repli sur le cliché si l'élément a disparu : mieux qu'un écran vide —
  // l'utilisateur ferme lui-même.
  const ficheOpVive = useMemo(
    () => (ficheOp ? operations.find(o => o.id === ficheOp.id) || ficheOp : null),
    [ficheOp, operations],
  );
  const ficheLigneVive = useMemo(
    () => (ficheLigne ? lignes.find(l => l.id === ficheLigne.id) || ficheLigne : null),
    [ficheLigne, lignes],
  );
  const ficheBudgetVif = useMemo(
    () => (ficheBudget ? budgets.find(b => b.id === ficheBudget.id) || ficheBudget : null),
    [ficheBudget, budgets],
  );
  // Une occurrence n'est pas stockée : elle se RECALCULE. La relire, c'est
  // relancer le moteur sur le mois concerné.
  const ficheOccVive = useMemo(() => {
    if (!ficheOcc) return null;
    const l = lignes.find(x => x.id === ficheOcc.ligneId);
    if (!l) return ficheOcc;
    return occurrencesDuMois({
      lignes: [l], actuals: operations, exceptions, rattachements, mk: ficheOcc.mois,
    })[0] || ficheOcc;
  }, [ficheOcc, lignes, operations, exceptions, rattachements]);

  // Les prévisions sans budget : visibles seulement s'il y en a.
  const lignesSansBudget = useMemo(
    () => lignes.filter(l => !l?.budgetId).length,
    [lignes],
  );
  const budgetOuvert = ouvert === SANS_BUDGET
    ? { id: null, nom: t('budget.sansBudget'), emoji: '❓' }
    : (ouvert ? budgets.find(b => b.id === ouvert) : null);

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      {/* L'IMPORT n'est plus ici : il a rejoint l'onglet Comptes le 10/08. Un
          relevé décrit des mouvements de compte, pas une intention budgétaire —
          et le laisser aux deux endroits entretiendrait la confusion. */}
      {/* Titre aligné sur l'onglet (« Budget »), via la clé DÉDIÉE :
          `famille.t.finances` reste réservée à la tuile du hub de My Calendar. */}
      <PageHeader titre={t('finances.ongletBudget')} iconeTitre="💰"
        /* Le MEME choix qu'en vue liste (Cedric, 15/08) : depuis cet ecran on
           ajoute soit une enveloppe, soit une prevision. Le ＋ demandait
           l'enveloppe sans demander — et il n'y avait aucun chemin d'ici vers
           une nouvelle prevision. */
        action={{ icone: '＋', label: t('commun.ajouter'),
          onPress: () => confirmerChoix(t('commun.ajouter'), '', [
            { text: t('budget.nouveauBudget'), onPress: () => setFeuilleB({ initial: null }) },
            { text: t('finances.nouvelleLigne'), onPress: () => setFeuilleL({ initial: null }) },
            { text: t('commun.annuler'), style: 'cancel' },
          ]) }}>
        {/* La bascule de SYNTHÈSE (16/08) : grisée quand la carte est cachée
            — l'icône montre l'état, l'appui le change. */}
        <TouchableOpacity onPress={basculerSynthese} hitSlop={HIT_MOIS}
          accessibilityLabel={t('finances.basculerSynthese')}>
          <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
        </TouchableOpacity>
        {/* Même bascule que dans une enveloppe, à la même place : 🧾 pour la
            vue LISTE — toutes les prévisions, tous budgets confondus, sans
            découpage par mois. */}
        <TouchableOpacity onPress={() => setMode('liste')} hitSlop={HIT_MOIS}
          accessibilityLabel={t('finances.vueListe')}>
          <Text style={{ fontSize: 19, marginLeft: 14 }}>🧾</Text>
        </TouchableOpacity>
      </PageHeader>

      {/* Même disposition qu'une enveloppe — carte, recherche, mois — et dans
          le même ordre : deux écrans qui font la même chose doivent se lire
          pareil. L'ensemble ne défile PAS : c'est la clé de lecture des
          totaux en dessous. */}
      <View style={s.enteteFixe}>
        {/* Les deux flux a DROITE du net (Cedric, 15/08) — a droite pour ne
            pas augmenter la hauteur de la carte. */}
        {syntheseVisible && (
        <View style={[s.totalCarte, { flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={s.totalLbl}>
              {t('finances.suiviPrevu')}  ·  {libelleMois(moisVu, t).toUpperCase()}
            </Text>
            <Text style={[s.totalVal, totalMois < 0 && { color: UI.danger || '#c0392b' },
              totalMois > 0 && { color: UI.ok || '#2e7d32' }]}>{formatEuro(totalMois)}</Text>
            <Text style={s.detailTotal}>
              {budgetsVus.length} {t('finances.ongletBudget').toLowerCase()}{budgetsVus.length > 1 ? 's' : ''}
            </Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={[s.fluxVal, { color: UI.ok || '#2e7d32' }]}>
              + {formatEuro(prevusParSens.credit)}
            </Text>
            <Text style={[s.fluxVal, { color: UI.danger || '#c0392b' }]}>
              − {formatEuro(prevusParSens.debit)}
            </Text>
          </View>
        </View>
        )}

        {/* Le modèle commun, complet (Cédric, 16/08) : le champ seul laissait
            cet écran sans tri ni filtre — les deux boutons manquaient. */}
        <BarreRecherche valeur={recherche} onChange={setRecherche}
          nbFiltres={filtreBudgets !== 'tous' ? 1 : 0} onFiltres={() => setCalque('filtres')}
          triActif={triBudgets !== 'defaut'} onTri={() => setCalque('tri')} />

      <View style={s.barreMois}>
        {/* « » : saut au mois voisin qui porte des échéances (15/08). */}
        <TouchableOpacity onPress={() => sautAvant && setMoisVu(sautAvant)} hitSlop={HIT_MOIS}
          disabled={!sautAvant}>
          <Text style={[s.navMois, !sautAvant && { opacity: 0.28 }]}>«</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setMoisVu(decalerMois(moisVu, -1))} hitSlop={HIT_MOIS}>
          <Text style={s.navMois}>‹</Text>
        </TouchableOpacity>
        <View style={s.titreMois}>
          <Text style={s.moisTxt}>{libelleMois(moisVu, t)}</Text>
          <TouchableOpacity onPress={() => setMoisVu(moisCourant())} hitSlop={HIT_MOIS}
            disabled={moisVu === moisCourant()}>
            <Text style={[s.cibleMois, moisVu === moisCourant() && { opacity: 0.25 }]}>🎯</Text>
          </TouchableOpacity>
        </View>
        <TouchableOpacity onPress={() => setMoisVu(decalerMois(moisVu, 1))} hitSlop={HIT_MOIS}>
          <Text style={s.navMois}>›</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => sautApres && setMoisVu(sautApres)} hitSlop={HIT_MOIS}
          disabled={!sautApres}>
          <Text style={[s.navMois, !sautApres && { opacity: 0.28 }]}>»</Text>
        </TouchableOpacity>
      </View>
      </View>

      <ScrollView contentContainerStyle={s.corps}>
        {budgets.length === 0 ? (
          <EmptyState icon="💰" texte={t('budget.aucunBudget')} aide={t('budget.payeursAide')} />
        ) : (
          /* ARBRE budget → prévisions (Cédric, 14/08). Les prévisions qui
             ne tombent pas ce mois-ci sont GRISÉES et non masquées : une
             ligne trimestrielle disparaissait onze mois sur douze, et on ne
             savait plus si elle existait encore. */
          <ArbreBudgetsMois budgets={budgetsVus} lignes={lignes}
            exceptions={exceptions} actuals={operations} rattachements={rattachements}
            mois={moisVu} prevuParBudget={prevuParBudget}
            sansBudget={recherche.trim() ? 0 : lignesSansBudget}
            onOuvrir={setOuvert}
            onOuvrirSansBudget={() => setOuvert(SANS_BUDGET)}
            onEditerBudget={(b) => setFeuilleB({ initial: b })}
            onSupprimerBudget={demanderSuppressionBudget}
            onFicheLigne={setFicheLigne}
            /* Les feuilles de l'arbre se gèrent au swipe (audit du 15/08). */
            onEditerLigne={(l) => setFeuilleL({ initial: l })}
            onSupprimerLigne={(l) => supprimerLigne(l.id, tenantId).catch(echec)} />
        )}
      </ScrollView>

      {/* L'intérieur d'une enveloppe, dans le MODE courant. Les deux vues
          montrent la même chose : « mois » range par mois et se navigue
          d'un mois à l'autre ; « liste » montre toutes les échéances d'un
          coup. C'est la même enveloppe, lue autrement. */}
      {!!budgetOuvert && !operationsDe && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          {mode === 'mois' ? (
            <EcranEnveloppe budget={budgetOuvert} lignes={lignes} exceptions={exceptions}
              actuals={operations} rattachements={rattachements}
              onFermer={() => setOuvert(null)}
              onNouvelle={() => setFeuilleL({ initial: { budgetId: budgetOuvert.id } })}
              onFiche={setFicheOcc}
              onFicheLigne={setFicheLigne}
              onEditerLigne={(l) => setFeuilleL({ initial: l })}
              onSupprimerLigne={(l) => supprimerLigne(l.id, tenantId).catch(echec)}
              onOperations={() => setMode('liste')} />
          ) : (
            <EcranConfigBudget budget={budgetOuvert} lignes={lignes}
              actuals={operations} exceptions={exceptions} rattachements={rattachements}
              onFermer={() => setOuvert(null)}
              onModeMois={() => setMode('mois')}
              onNouvelle={() => setFeuilleL({ initial: { budgetId: budgetOuvert.id } })}
              onFiche={setFicheLigne}
              onEditerLigne={(l) => setFeuilleL({ initial: l })}
              onSupprimerLigne={(l) => supprimerLigne(l.id, tenantId).catch(echec)} />
          )}
        </View>
      )}

      {/* Vue LISTE au niveau RACINE : toutes les prévisions, tous budgets
          confondus. Pas de bouton retour — c'est le haut de l'onglet, il n'y
          a rien au-dessus. */}
      {!budgetOuvert && !operationsDe && mode === 'liste' && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranConfigBudget toutes lignes={lignes} budgets={budgets}
            actuals={operations} exceptions={exceptions} rattachements={rattachements}
            onNouveauBudget={() => setFeuilleB({ initial: null })}
            /* Les budgets se gèrent AUSSI d'ici (Cédric, 15/08) : tap = fiche,
               swipe → = éditer, swipe ← = supprimer (verrouillé si lignes). */
            onFicheBudget={setFicheBudget}
            onEditerBudget={(b) => setFeuilleB({ initial: b })}
            onSupprimerBudget={demanderSuppressionBudget}
            onModeMois={() => setMode('mois')}
            onNouvelle={() => setFeuilleL({ initial: null })}
            onFiche={setFicheLigne}
            onEditerLigne={(l) => setFeuilleL({ initial: l })}
            onSupprimerLigne={(l) => supprimerLigne(l.id, tenantId).catch(echec)} />
        </View>
      )}

      {/* Jamais vrai depuis le 11/08 — voir la note sur l'import d'EcranBudget. */}
      {!!operationsDe && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranBudget budget={budgets.find(b => b.id === operationsDe)} operations={opsDe(operationsDe)} membres={membres}
            onFermer={() => setOperationsDe(null)}
            onNouvelle={() => setFeuilleO({ initial: null, budgetId: operationsDe })}
            onFiche={setFicheOp}
            onEditer={(o) => setFeuilleO({ initial: o })}
            onSupprimer={(o) => supprimerOperation(o.id, o)}
            onRembourser={rembourser(budgets.find(b => b.id === operationsDe) || {})} />
        </View>
      )}

      {/* Même fiche que dans Suivi. Ici l'écran ne charge pas le réel : la
          fiche n'affiche donc aucun rattachement, et c'est honnête — le
          détail du réel se lit dans Suivi. */}
      <FicheOccurrence visible={!!ficheOcc} occurrence={ficheOccVive} actuals={operations} rattachements={rattachements}
        nomBudget={(budgets.find(b => b.id === ficheOcc?.ligne?.budgetId) || {}).nom || ''}
        onFermer={() => { setFicheOcc(null); retour.revenir(); }}
        onEditer={() => { const o = ficheOccVive; retour.empiler(() => setFicheOcc(o)); setFeuilleL({ initial: o.ligne }); setFicheOcc(null); }}
        onDetacher={(r) => detacher(r.id).catch(echec)}
        onRattacherOperation={() => { const o = ficheOcc; retour.empiler(() => setFicheOcc(o)); setOccARattacher(o); setFicheOcc(null); }} />

      {/* Rattacher depuis BUDGET, et pas seulement depuis Suivi (Cédric,
          12/08). L'occurrence visée porte son propre mois : on n'est donc pas
          contraint par celui qu'affiche l'écran — c'est ce que « rattacher
          indépendamment du mois » veut dire ici. */}
      <FeuilleRattachement visible={!!occARattacher} source={occARattacher} depuisOccurrence
        /* Pour ELARGIR au-dela des propositions (Cedric, 13/08). */
        operations={operations} rattachements={rattachements} comptes={comptes}
        candidats={occARattacher
          ? proposerOperations({ occurrence: occARattacher, operations, rattachements })
          : []}
        onValider={(c, montant) => {
          creerRattachement({
            depense: c.operation, rattachements, espaceId: tenantId,
            ligneId: occARattacher.ligneId, mois: occARattacher.mois,
            montant, origine: 'propose', creePar: user?.uid,
          }).catch(echec);
          setOccARattacher(null);
          retour.revenir();
        }}
        /* Ce qui est DÉJÀ rattaché à ce mois, et de quoi le défaire. */
        dejaRattaches={occARattacher
          ? rattachementsAvecOperation(rattachements, operations, occARattacher.ligneId, occARattacher.mois)
          : []}
        onDetacher={(r) => detacher(r.id).catch(echec)}
        /* « Plus rien à ajouter » : une ANNOTATION du mois, pas un
           rattachement. Le moteur met alors le reste à dépenser à zéro. */
        soldee={!!occARattacher && estSoldee(exceptions, occARattacher.ligneId, occARattacher.mois)}
        onSolder={(v) => {
          const { ligneId, mois } = occARattacher;
          (v
            ? definirException({ espaceId: tenantId, ligneId, mois, statut: 'soldee', creePar: user?.uid })
            : retirerException(ligneId, mois)
          ).catch(echec);
          // On NE ferme PAS : le cadenas est une icone d'ETAT dans la carte,
          // on doit voir l'etat changer sur place plutot qu'etre ejecte.
        }}
        onFermer={() => { setOccARattacher(null); retour.revenir(); }} />

      {/* La fiche d'une operation reelle : ses infos, et CE QU'ELLE HONORE.
          C'est ici que se lisent les rattachements cote Budget — decision de
          Cedric du 11/08. */}
      <FicheOperation visible={!!ficheOp} operation={ficheOpVive} rattachements={rattachements} lignes={lignes}
        nomBudget={(budgets.find(b => b.id === ficheOp?.budgetId) || {}).nom || ''}
        budgets={budgets}
        onFermer={() => { setFicheOp(null); retour.revenir(); }}
        onEditer={() => { const o = ficheOpVive; retour.empiler(() => setFicheOp(o)); setFeuilleO({ initial: o }); setFicheOp(null); }}
        onDetacher={(r) => detacher(r.id).catch(echec)}
        /* Le ＋ du titre et l'édition d'une part (16/08) — mêmes gestes que
           depuis les Comptes. */
        onRattacher={(op) => { const o = ficheOp; retour.empiler(() => setFicheOp(o)); setOpARattacher(op); setFicheOp(null); }}
        onModifierPart={(r, montant) => modifierRattachement(r.id, {
          depense: ficheOpVive, rattachements, ligneId: r.ligneId, mois: r.mois, montant,
        }).catch(echec)} />

      {/* Rattacher une OPÉRATION à une ou plusieurs lignes — la même feuille
          multi-sélection que Comptes et Suivi. Écritures en SÉQUENCE : chaque
          part doit voir les précédentes. */}
      <FeuilleRattacherLignes visible={!!opARattacher} operation={opARattacher}
        lignes={lignes} budgets={budgets} operations={operations}
        exceptions={exceptions} rattachements={rattachements}
        onValider={async (parts) => {
          const op = opARattacher;
          setOpARattacher(null); retour.revenir();
          try {
            const faits = [];
            for (const p of parts) {
              const id = await creerRattachement({
                depense: op, rattachements: [...rattachements, ...faits], espaceId: tenantId,
                ligneId: p.occurrence.ligneId, mois: p.occurrence.mois,
                montant: p.montant, origine: 'manuel', creePar: user?.uid,
              });
              faits.push({ id, depenseId: op.id,
                ligneId: p.occurrence.ligneId, mois: p.occurrence.mois, montant: p.montant });
            }
          } catch (e) { echec(e); }
        }}
        onFermer={() => { setOpARattacher(null); retour.revenir(); }} />

      {/* Rattachement en LOT (spéc. du 12/08) : plusieurs opérations sur une
          même prévision, avec filtres, tri et répartition. Les mois proposés
          sont les échéances RÉELLEMENT dues — c'est ce qui permet de repérer
          une opération « hors plage » (§9.1). */}
      <FeuilleRattacherOperations visible={!!lotPour} ligne={lotPour}
        moisDus={moisDusDe(lotPour)}
        reference={lotPour ? montantPrevu(lotPour, moisCourant()) : 0}
        operations={operations} rattachements={rattachements} lignes={lignes} comptes={comptes}
        onValider={({ diff, moisChoisi }) => {
          appliquerDiffRattachements({
            espaceId: tenantId, ligneId: lotPour.id, operations, rattachements,
            ajouts: diff.ajouts, retraits: diff.retraits,
            moisParOperation: moisChoisi, creePar: user?.uid,
          }).then(r => {
            setLotPour(null);
            retour.revenir();
            // §10.6 — compte rendu non bloquant : la division des parts a pu
            // toucher d'autres prévisions, l'utilisateur doit le savoir.
            const autres = r.lignesRecalculees
              ? t('finances.toastAutres', { n: r.lignesRecalculees }) : '';
            Alert.alert('', t('finances.toastRattache', { n: r.ajoutes, autres }));
          }).catch(echec);
        }}
        onFermer={() => { setLotPour(null); retour.revenir(); }} />

      {/* Fiche d'une PRÉVISION (hors mois) : sa configuration et surtout ses
          prochaines échéances réelles — le seul endroit qui montre qu'une
          ligne mal réglée ne tombera jamais. */}
      <FicheLignePrevue visible={!!ficheLigne} ligne={ficheLigneVive}
        actuals={operations} rattachements={rattachements} exceptions={exceptions}
        nomBudget={(budgets.find(b => b.id === ficheLigne?.budgetId) || {}).nom || ''}
        onFermer={() => { setFicheLigne(null); retour.revenir(); }}
        onEditer={() => { const l = ficheLigneVive; retour.empiler(() => setFicheLigne(l)); setFeuilleL({ initial: l }); setFicheLigne(null); }}
        onRattacherEcheance={(o) => { const l = ficheLigne; retour.empiler(() => setFicheLigne(l)); setOccARattacher(o); setFicheLigne(null); }}
        onRattacherLot={() => { const l = ficheLigne; retour.empiler(() => setFicheLigne(l)); setLotPour(l); setFicheLigne(null); }}
        /* Les actions sur les operations rattachees (les feuilles de l'arbre).
           Detacher retire la PART, pas l'operation : elle redevient un imprevu
           visible dans Comptes, elle ne disparait pas. */
        onDetacherPart={(f) => detacher(f.id).catch(echec)}
        onEditerOperation={(f) => {
          const l = ficheLigne;
          retour.empiler(() => setFicheLigne(l));
          setFeuilleO({ initial: f.operation }); setFicheLigne(null);
        }}
        /* Deplacer vers un autre mois = rouvrir le rattachement en LOT, ou le
           mois de chaque operation se choisit. Pas de troisieme ecran pour
           une chose que celui-la fait deja. */
        onDeplacerPart={() => {
          const l = ficheLigne;
          retour.empiler(() => setFicheLigne(l));
          setLotPour(l); setFicheLigne(null);
        }}
        onFicheOperation={(f) => {
          const l = ficheLigne;
          retour.empiler(() => setFicheLigne(l));
          setFicheOp(f.operation); setFicheLigne(null);
        }}
        /* TOUT detacher sur un mois : une confirmation, parce que le geste
           defait d'un coup ce que plusieurs rattachements ont construit. */
        onToutDetacher={(o) => confirmerSuppression(
          t('finances.toutDetacher'), libelleMois(o.mois, t),
          () => {
            const parts = rattachements.filter(r => r?.ligneId === o.ligneId && r.mois === o.mois);
            Promise.all(parts.map(r => detacher(r.id))).catch(echec);
          })}
        /* Verrouiller / rouvrir : la MEME bascule que le cadenas de la feuille
           de rattachement. Un seul etat, deux chemins pour l'atteindre. */
        onVerrouiller={(o) => {
          const clos = estSoldee(exceptions, o.ligneId, o.mois);
          (clos
            ? retirerException(o.ligneId, o.mois)
            : definirException({ espaceId: tenantId, ligneId: o.ligneId, mois: o.mois, statut: 'soldee', creePar: user?.uid })
          ).catch(echec);
        }} />

      {/* La carte d'identité d'une enveloppe : ses flux, ses payeurs. Le tap
          informe — l'édition reste au swipe et au ✏️ de la fiche (15/08). */}
      <FicheBudget visible={!!ficheBudget} budget={ficheBudgetVif} lignes={lignes}
        onFermer={() => { setFicheBudget(null); retour.revenir(); }}
        onEditer={() => {
          const b = ficheBudgetVif;
          retour.empiler(() => setFicheBudget(b));
          setFeuilleB({ initial: b }); setFicheBudget(null);
        }} />

      <FeuilleBudget visible={!!feuilleB} initial={feuilleB?.initial} membres={membres}
        ownerUid={espaceActif?.creePar} onEnregistrer={enregistrerBudget}
        onFermer={() => { setFeuilleB(null); retour.revenir(); }} />
      <FeuilleOperation configCategories={configCategories} visible={!!feuilleO} initial={feuilleO?.initial} membres={membres} monUid={user?.uid}
        onDemanderDate={setDateReq} onEnregistrer={enregistrerOperation} onFermer={() => { setFeuilleO(null); retour.revenir(); }} />
      {/* La création d'une ligne prévue vit DÉSORMAIS ici : planifier est le
          rôle de l'onglet Budget. Elle reste aussi accessible depuis Suivi
          tant que cet écran n'est pas refait (étape 4) — deux portes valent
          mieux qu'aucune pendant la transition. */}
      <FeuilleLigne configCategories={configCategories} visible={!!feuilleL} initial={feuilleL?.initial} budgets={budgets}
        onDemanderMois={(valeur, onChoisir) => setDateReq({ valeur, onChoisir, moisSeul: true })}
        onEnregistrer={enregistrerLigne} onFermer={() => { setFeuilleL(null); retour.revenir(); }} />
      {/* Filtre et tri des budgets. Le sélecteur de date reste rendu en
          DERNIER — l'ordre des Modal sœurs décide de qui passe devant. */}
      <CalqueChoix visible={calque === 'filtres'} titre={t('finances.filtrer')} emoji="🔽"
        onFermer={() => setCalque(null)}>
        {[
          { id: 'tous', lbl: t('finances.tousBudgets') },
          { id: 'echeances', lbl: t('finances.filtreEcheancesMois') },
        ].map(x => (
          <TouchableOpacity key={x.id} style={[s.choix, filtreBudgets === x.id && s.choixOn]}
            activeOpacity={0.7} onPress={() => { setFiltreBudgets(x.id); setCalque(null); }}>
            <Text style={s.choixTxt}>{x.lbl}</Text>
            {filtreBudgets === x.id && <Text style={s.choixCoche}>✓</Text>}
          </TouchableOpacity>
        ))}
      </CalqueChoix>
      <CalqueChoix visible={calque === 'tri'} titre={t('finances.tri')} emoji="↕"
        onFermer={() => setCalque(null)}>
        {[
          { id: 'defaut', lbl: t('finances.triDefaut') },
          { id: 'nom', lbl: t('finances.tri_libelle') },
          { id: 'prevu', lbl: t('finances.tri_montant') },
        ].map(x => (
          <TouchableOpacity key={x.id} style={[s.choix, triBudgets === x.id && s.choixOn]}
            activeOpacity={0.7} onPress={() => { setTriBudgets(x.id); setCalque(null); }}>
            <Text style={s.choixTxt}>{x.lbl}</Text>
            {triBudgets === x.id && <Text style={s.choixCoche}>✓</Text>}
          </TouchableOpacity>
        ))}
      </CalqueChoix>

      {/* `moisSeul` quand la demande vient d'un champ MOIS (debut/fin
          d'une prevision) : choisir un jour qu'on jetterait ensuite serait
          une fausse precision. */}
      <SelecteurDate visible={!!dateReq} valeur={dateReq?.valeur} avecEffacer
        moisSeul={!!dateReq?.moisSeul}
        onChange={(dk) => dateReq?.onChoisir(dk)} onFermer={() => setDateReq(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  enteteFixe: { paddingHorizontal: 16, paddingTop: 12, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 14, marginBottom: 10 },
  totalLbl: { fontSize: 11.5, color: UI.faint, fontWeight: '800', letterSpacing: 0.4 },
  totalVal: { fontSize: 26, fontWeight: '800', color: UI.ink, marginTop: 2 },
  detailTotal: { fontSize: 12.5, color: UI.muted, fontWeight: '700', marginTop: 2 },
  fluxVal: { fontSize: 14.5, fontWeight: '800', marginTop: 1 },
  controles: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  rechercheZone: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: UI.card, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12 },
  recherche: { flex: 1, paddingVertical: 10, fontSize: 14, color: UI.ink },
  barreMois: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  titreMois: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  moisTxt: { fontSize: 16, fontWeight: '800', color: UI.ink },
  cibleMois: { fontSize: 15 },
  navMois: { fontSize: 24, color: UI.accent, fontWeight: '300', width: 36, textAlign: 'center' },
  carteSans: { borderStyle: 'dashed', marginBottom: 8 },
  nom: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 2 },
  total: { fontSize: 15, fontWeight: '800', color: UI.ink },
  choix: { flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 11, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 6 },
  choixOn: { borderColor: UI.accent, borderWidth: 1.5 },
  choixTxt: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '700' },
  choixCoche: { fontSize: 15, color: UI.accent, fontWeight: '800' },
});
